/**
 * Qibla direction utilities.
 * The Kaaba is at 21.4225°N, 39.8262°E (Masjid al-Haram, Mecca).
 *
 * Bearing is computed with the great-circle formula and reported as
 * degrees clockwise from True North (0° = N, 90° = E, 180° = S, 270° = W).
 *
 * Distance uses the spherical law of cosines (good enough for ≤ 0.5%
 * error at any earth distance — perfectly fine for a Qibla display).
 */

const KAABA_LAT = 21.4225;
const KAABA_LON = 39.8262;
const EARTH_RADIUS_KM = 6371;

const toRad = (deg) => (deg * Math.PI) / 180;
const toDeg = (rad) => (rad * 180) / Math.PI;

/**
 * Great-circle initial bearing from (lat, lon) to the Kaaba.
 * Returns degrees in [0, 360).
 */
export function qiblaBearing(lat, lon) {
  const φ1 = toRad(lat);
  const φ2 = toRad(KAABA_LAT);
  const Δλ = toRad(KAABA_LON - lon);

  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const θ = Math.atan2(y, x);
  return (toDeg(θ) + 360) % 360;
}

/**
 * Great-circle distance in km from (lat, lon) to the Kaaba.
 */
export function distanceToKaaba(lat, lon) {
  const φ1 = toRad(lat);
  const φ2 = toRad(KAABA_LAT);
  const Δλ = toRad(KAABA_LON - lon);
  const cosC = Math.sin(φ1) * Math.sin(φ2) + Math.cos(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  // clamp to avoid acos domain errors at antipode
  const clamped = Math.max(-1, Math.min(1, cosC));
  return EARTH_RADIUS_KM * Math.acos(clamped);
}

/**
 * Convert a bearing (0–360) to one of 16 compass-point keys.
 * Caller maps the key to a localised string.
 */
export function bearingToCompassPoint(bearing) {
  const points = [
    'N', 'NNE', 'NE', 'ENE',
    'E', 'ESE', 'SE', 'SSE',
    'S', 'SSW', 'SW', 'WSW',
    'W', 'WNW', 'NW', 'NNW',
  ];
  const idx = Math.round(bearing / 22.5) % 16;
  return points[idx];
}
